// src/features/organisation/components/competences/CompetenceManagement.tsx
import React, { useState } from 'react';
import { AlertTriangle, Award, CheckCircle, Clock, XCircle, Plus, Trash2 } from 'lucide-react';
import { useCompetences } from '../../hooks/useCompetences';
import { Competence } from '../../../../types/organisation/competences.types';
import { TableColumn } from '../../../../types';
import DataTable from '../../../../components/ui/DataTable';
import CompetenceDetailForm from './CompetenceDetailForm';
import OrganisationNavigation from '../OrganisationNavigation';

const CompetenceManagement: React.FC = () => {
  const { competences, loading, error, refresh, deleteCompetence } = useCompetences();
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Competence[]>([]);

  const columns: TableColumn<Competence>[] = [
    {
      key: 'employee',
      title: 'Employé',
      sortable: true,
      render: (value: unknown) => value ? `${value.firstName} ${value.lastName}` : '-',
    },
    {
      key: 'competenceName',
      title: 'Compétence',
      sortable: true,
    },
    {
      key: 'competenceLevel',
      title: 'Niveau',
      render: (value: string) => {
        const colors: Record<string, string> = {
          'Expert': 'bg-purple-100 text-purple-800',
          'Confirmé': 'bg-blue-100 text-blue-800',
          'Débutant': 'bg-gray-100 text-gray-800',
          'Certifié': 'bg-green-100 text-green-800',
          'Expérimenté': 'bg-orange-100 text-orange-800',
          'Qualifié': 'bg-blue-100 text-blue-800',
        };
        return value ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        ) : null;
      },
    },
    {
      key: 'certifyingBody',
      title: 'Organisme',
      render: (value: string) => value || '-',
    },
    {
      key: 'certificationDate',
      title: 'Date certification',
      render: (value: string) => value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
    {
      key: 'expiryDate',
      title: 'Expiration',
      render: (value: string) => value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
    {
      key: 'status',
      title: 'Statut',
      render: (value: string) => {
        const colors: Record<string, string> = {
          'Valide': 'bg-green-100 text-green-800',
          'Expire bientôt': 'bg-yellow-100 text-yellow-800',
          'Expiré': 'bg-red-100 text-red-800',
        };
        return value ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value === 'Expiré' && <AlertTriangle className="h-3 w-3 mr-1" />}
            {value}
          </span>
        ) : null;
      },
    },
  ];

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedRows.length} compétence(s) ?`)) return;
    try {
      await Promise.all(selectedRows.map(c => deleteCompetence(c.id)));
      setSelectedRows([]);
      await refresh();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleClose = async () => {
    setSelectedId(undefined);
    await refresh();
  };

  const expiringSoon = (competences || []).filter(c => c.status === 'Expire bientôt');
  const expired = (competences || []).filter(c => c.status === 'Expiré');

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Award className="h-8 w-8 text-pink-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Compétences</h1>
            <p className="text-gray-600">Suivez les compétences et certifications</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRows.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              <span>Supprimer ({selectedRows.length})</span>
            </button>
          )}
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Nouvelle compétence</span>
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(competences || []).filter(c => c.status === 'Valide').length}
              </div>
              <div className="text-sm text-gray-600">Valides</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{expiringSoon.length}</div>
              <div className="text-sm text-gray-600">Expirent bientôt</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-red-500 rounded-md p-3">
              <XCircle className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{expired.length}</div>
              <div className="text-sm text-gray-600">Expirées</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-pink-500 rounded-md p-3">
              <Award className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{(competences || []).length}</div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Alertes */}
      {(expiringSoon.length > 0 || expired.length > 0) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Alertes d'expiration</h3>
          <div className="space-y-2">
            {expired.map(c => (
              <div key={c.id} className="flex items-center p-3 bg-red-50 rounded border border-red-200">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" />
                <span className="text-sm text-red-800 font-medium">{c.competenceName}</span>
                <span className="text-sm text-red-700 ml-2">— {c.employee ? `${c.employee.firstName} ${c.employee.lastName}` : ''}</span>
                <span className="ml-auto text-xs text-red-600">Expirée</span>
              </div>
            ))}
            {expiringSoon.map(c => (
              <div key={c.id} className="flex items-center p-3 bg-yellow-50 rounded border border-yellow-200">
                <Clock className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0" />
                <span className="text-sm text-yellow-800 font-medium">{c.competenceName}</span>
                <span className="text-sm text-yellow-700 ml-2">— {c.employee ? `${c.employee.firstName} ${c.employee.lastName}` : ''}</span>
                <span className="ml-auto text-xs text-yellow-600">Expire bientôt</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-700">Erreur lors du chargement des compétences : {error.message}</span>
          <button onClick={refresh} className="ml-auto text-sm text-red-600 underline hover:text-red-800">Réessayer</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200">
        <DataTable
          data={competences || []}
          columns={columns}
          loading={loading}
          selectable={true}
          onSelectionChange={setSelectedRows}
          onRowDoubleClick={(comp) => setSelectedId(comp.id)}
          emptyMessage="Aucune compétence trouvée"
        />
      </div>

      {selectedId !== undefined && (
        <CompetenceDetailForm
          competenceId={selectedId}
          onClose={handleClose}
          onSave={() => refresh()}
        />
      )}
    </div>
  );
};

export default CompetenceManagement;
